import type { TimestampInMillis } from './dates';
import type { UUID } from './identifier';

export const DEFAULT_FUEL_CONSUMPTION = 8; // assuming 8 km/l
export const DEFAULT_GAS_PRICE = 4.567;
export const LITERS_PER_IDLE_HOUR = 1.5; // assuming 1,5 l/h
export const LITERS_PER_IDLE_SECOND = LITERS_PER_IDLE_HOUR / (60 * 60);

export interface FuelTransaction {
  id: UUID;
  createdBy: string;
  transactionCode?: string;
  timeInMillis: TimestampInMillis;
  vehicleId: UUID;
  licensePlate?: string;
  fuelType: string;
  totalCost: number;
  fuelLiters: number;
  cardDrivenMeters: number;
  cobliDrivenMeters?: number;
  vehicleModel?: string;
  deletedAt?: TimestampInMillis;
}

export interface FuelCardVehicle {
  id: UUID;
  vehicleId: UUID;
  integrationId: UUID;
  licensePlate?: string;
  model?: string;
  refConsumption?: number;
}

interface FuelTransactionBodyContentBase {
  transactionTime: string;
  vehicleId: string;
  odometerInKm?: number;
  driverName?: string;
  fuelType: string;
  totalCost: number;
  quantityInLiters: number;
  merchant?: Merchant;
}

export type FuelTransactionAttachment =
  | {
      /** Base64-encoded receipt bytes, without the `data:...;base64,` prefix. */
      fileBase64: string;
      /** MIME type of the receipt. */
      fileContentType: string;
    }
  | {
      fileBase64?: undefined;
      fileContentType?: undefined;
    };

/** Optional odometer photo attachment — same base64 envelope as the receipt. */
export interface FuelTransactionOdometerAttachment {
  odometerFileBase64?: string;
  odometerFileContentType?: string;
}

export type FuelTransactionBodyContent = FuelTransactionBodyContentBase & FuelTransactionAttachment & FuelTransactionOdometerAttachment;

export interface Merchant {
  name?: string;
  state?: string;
  city?: string;
  cnpj?: string;
}

export interface TransactionVehicleGroup {
  id: UUID;
  name: String;
}

export interface TransactionVehicle {
  id: UUID;
  licensePlate: string;
  model: string;
  group: TransactionVehicleGroup;
  alias?: string;
}

export interface Transaction {
  id: UUID;
  transactionTime: number;
  transactionCode: string;
  vehicle: TransactionVehicle;
  fuelType: string;
  costPerLiter: number;
  fuelLiters: number;
  totalCost: number;
  cobliDrivenKms: number;
  createdBy: string;
  creatorId?: UUID;
  totalCount: number;
  fileUrl: string | null;
  /** How the entry got in. Null on rows written before the column existed. */
  source?: string | null;
}

/**
 * How the Origin column and the drawer label a fuel entry.
 *
 * `driver_app` is kept for rows written before the `source` column existed: there the AI and the
 * manual flow are indistinguishable, so they must not be guessed into either one.
 */
export type OriginType = 'manual' | 'driver_app' | 'driver_app_ai' | 'driver_app_manual' | 'integration';

/**
 * Narrows an app-created entry into the AI or the manual flow.
 *
 * Anything the panel does not recognise — a legacy null, or an origin a newer backend starts
 * writing — falls back to the generic app label instead of breaking the row.
 */
export const driverAppOriginType = (source?: string | null): OriginType => {
  if (source === 'DriverAppManual') return 'driver_app_manual';
  if (source === 'DriverAppAi') return 'driver_app_ai';
  return 'driver_app';
};

export interface PagedTransactions {
  content: Transaction[];
  number: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface TransactionsSummary {
  avgFuelConsumption: number;
  avgCostPerKm: number;
  sumTotalCost: number;
}

export interface TransactionStatsByVehicleSummary {
  sumCobliDrivenKms: number;
  avgCostPerLiter: number;
  sumFuelLiters: number;
}

/**
 * Resposta de `/transactions/stats-by-vehicle/summary/grouped`: um item por bucket
 * (semana/mês). Buckets sem dados não vêm no array — o join por `startInterval`
 * precisa tratar a ausência.
 */
export interface TransactionStatsByVehicleSummaryByPeriod {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  /** Backend nomeia este campo `sum_driven_kms` no `/summary/grouped`, diferente do `/summary`
   * agregado que usa `sum_cobli_driven_kms`. Mantemos o nome do contrato. */
  sumDrivenKms: number;
  avgCostPerLiter: number;
  sumFuelLiters: number;
}

export interface TransactionsAvgFuelConsumptionAndCostPerKm {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  avgFuelConsumption: number;
  avgCostPerKm: number;
  vehicleLicensePlate: string;
  chartGroup: string;
}

export interface TransactionsSumTotalCostByFuelType {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  fuelType: string;
  totalCost: number;
  totalLiters: number;
}

export interface TransactionStatsByVehicle {
  vehicleId: UUID;
  vehicleLicensePlate: string;
  vehicleModel: string;
  vehicleGroup: string;
  vehicleGroupName: string;
  sumTotalCost: number;
  avgCostPerLiter: number;
  avgCostPerKm: number;
  sumFuelLiters: number;
  sumCobliDrivenKms: number;
  consumptionAvg: number;
  consumptionGoal: number;
  fuelType: string;
  totalCount: number;
}

export interface PagedTransactionStatsByVehicle {
  content: TransactionStatsByVehicle[];
  number: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface TransactionsHistoryBodyContent {
  startDate: Date;
  endDate: Date;
  searchQuery?: String;
  fuelTypes?: String[];
  vehicleIds?: UUID[];
  vehicleGroupIds: UUID[];
  vehicleSizes?: String[];
  tagNames?: String[];
  sortField: String;
  sortDirection: String;
  page: Number;
  size: Number;
}

export interface TransactionSummaryBodyContent {
  startDate: Date;
  endDate: Date;
  searchQuery?: string;
  fuelTypes?: string[];
  vehicleIds?: UUID[];
  vehicleGroupIds: UUID[];
  vehicleSizes?: string[];
  tagNames?: string[];
}

export type GroupPeriod = 'WEEK' | 'MONTH';

export interface TransactionSummaryBodyContentByPeriodType extends TransactionSummaryBodyContent {
  groupPeriod: GroupPeriod;
}

export const toFormattedComsumptionInLiters = (value: number, minimumFractionDigits: number, maximumFractionDigits: number): string =>
  Intl.NumberFormat('pt-BR', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
